<template>
  <div class="ph-list">
    <div class="ph-list-head">
      <h6 class="ph-list-title">{{ title }}</h6>
      <span class="ph-list-count">{{ values.length }} фраз</span>
    </div>

    <div v-if="isGood" class="ph-rows">
      <div class="ph-rows-head">
        <span class="ph-rows-head-phrase">Фраза</span>
        <span class="ph-rows-head-freq">Частотность</span>
      </div>
      <div class="ph-row" v-for="(row, idx) in rows" :key="idx">
        <input type="text" class="ph-row-phrase" v-model="row.value" placeholder="Фраза"/>
        <input type="number" class="ph-row-freq" v-model="row.freq" placeholder="—" min="0"/>
        <button type="button" class="ph-row-del" title="Удалить строку" @click="removeRow(idx)">✕</button>
      </div>
      <button type="button" class="ph-btn ph-btn-ghost ph-row-add" @click="addRow">+ Добавить фразу</button>
    </div>
    <textarea
      v-else
      class="ph-list-area"
      v-model="text"
      placeholder="По одной фразе в строке"
      rows="12"
    ></textarea>

    <div class="ph-list-actions">
      <button type="button" class="ph-btn" :disabled="busy" @click="save">Сохранить</button>
      <button type="button" class="ph-btn ph-btn-ghost" :disabled="busy" @click="pickFile">Загрузить из Excel</button>
      <button type="button" class="ph-btn ph-btn-ghost" :disabled="busy || !values.length" @click="download">Скачать</button>
      <button type="button" class="ph-btn ph-btn-ghost" :disabled="busy || !values.length" @click="clearAll">Очистить список</button>
      <input ref="file" class="ph-list-file" type="file" accept=".xlsx,.xls" @change="onFile"/>
    </div>

    <p v-if="message" class="ph-list-msg">{{ message }}</p>
    <p v-if="error" class="ph-list-err">{{ error }}</p>

    <div v-if="pendingUpload" class="ph-modal-backdrop" @click.self="cancelUpload">
      <div class="ph-modal">
        <p>В файле {{ pendingUpload.length }} фраз. Что сделать со списком «{{ title }}»?</p>
        <div class="ph-modal-actions">
          <button type="button" class="ph-btn" @click="applyUpload('append')">Дополнить</button>
          <button type="button" class="ph-btn" @click="applyUpload('replace')">Заменить</button>
          <button type="button" class="ph-btn ph-btn-ghost" @click="cancelUpload">Отмена</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { readPhraseColumn, downloadPhraseList, readPhraseRows, downloadPhraseRows } from '@/utils/phraseExcel';

export default {
  name: 'PhraseListEditor',
  props: {
    projectId: { type: String, default: '' },
    setId: { type: String, default: '' },
    kind: { type: String, required: true },
    title: { type: String, required: true },
  },
  emits: ['saved'],
  data() {
    return {
      hostApi: process.env.VUE_APP_API_URL,
      values: [],
      text: '',
      rows: [],
      busy: false,
      message: '',
      error: '',
      pendingUpload: null,
      savedText: '',
      savedRows: [],
    };
  },
  computed: {
    isGood() {
      return this.kind === 'good';
    },
    isDirty() {
      return this.isGood
        ? JSON.stringify(this.rows) !== JSON.stringify(this.savedRows)
        : this.text !== this.savedText;
    },
  },
  watch: {
    setId: 'load',
  },
  mounted() {
    this.load();
  },
  methods: {
    async load() {
      if (!this.projectId || !this.setId) return;
      this.error = '';
      try {
        const res = await axios.post(this.hostApi + '/api/v1/phraseset/getPhrases', {
          clientId: localStorage.getItem('clientId'),
          projectId: this.projectId,
          setId: this.setId,
          kind: this.kind,
        });
        this.values = res.data.data.values || [];
        if (this.isGood) {
          const freq = res.data.data.freq || {};
          this.rows = this.values.map(v => ({ value: v, freq: freq[v] !== undefined ? freq[v] : '' }));
          this.savedRows = JSON.parse(JSON.stringify(this.rows));
        } else {
          this.text = this.values.join('\n');
          this.savedText = this.text;
        }
      } catch (e) {
        this.error = 'Не удалось загрузить список';
      }
    },
    async send(values) {
      this.busy = true;
      this.error = '';
      this.message = '';
      try {
        const res = await axios.post(this.hostApi + '/api/v1/phraseset/setPhrases', {
          clientId: localStorage.getItem('clientId'),
          projectId: this.projectId,
          setId: this.setId,
          kind: this.kind,
          values: values,
        });
        const data = res.data.data;
        this.message = 'Сохранено ' + data.saved + ' фраз'
          + (data.skipped ? ', отброшено ' + data.skipped : '');
        await this.load();
        this.$emit('saved');
      } catch (e) {
        // Валидация с бэка приходит как 400 с полем error.
        this.error = (e.response && e.response.data && e.response.data.error) || 'Не удалось сохранить список';
      } finally {
        this.busy = false;
      }
    },
    save() {
      if (this.isGood) {
        this.send(this.rows.filter(r => String(r.value || '').trim()));
      } else {
        this.send(this.text.split('\n'));
      }
    },
    addRow() {
      this.rows.push({ value: '', freq: '' });
    },
    removeRow(idx) {
      this.rows.splice(idx, 1);
    },
    pickFile() {
      this.$refs.file.click();
    },
    async onFile(event) {
      const file = event.target.files && event.target.files[0];
      event.target.value = '';
      if (!file) return;
      try {
        this.pendingUpload = this.isGood ? await readPhraseRows(file) : await readPhraseColumn(file);
      } catch (e) {
        this.error = e.message;
      }
    },
    applyUpload(mode) {
      const current = this.isGood ? this.rows : this.values;
      const values = mode === 'append' ? this.pendingUpload.concat(current) : this.pendingUpload;
      this.pendingUpload = null;
      this.send(values);
    },
    cancelUpload() {
      this.pendingUpload = null;
    },
    clearAll() {
      if (!window.confirm('Очистить весь список «' + this.title + '»? Действие нельзя отменить.')) return;
      this.send([]);
    },
    async download() {
      try {
        if (this.isGood) {
          await downloadPhraseRows(this.rows, this.title);
        } else {
          await downloadPhraseList(this.values, this.title);
        }
      } catch (e) {
        this.error = 'Не удалось скачать файл';
      }
    },
  },
};
</script>

<style scoped>
.ph-list { display: flex; flex-direction: column; gap: 8px; }
.ph-list-head { display: flex; align-items: baseline; justify-content: space-between; }
.ph-list-title { margin: 0; font-size: 15px; }
.ph-list-count { font-size: 12px; opacity: 0.6; }
.ph-list-area { width: 100%; font-family: inherit; font-size: 13px; padding: 8px; }
.ph-rows { display: flex; flex-direction: column; gap: 4px; max-height: 320px; overflow-y: auto; }
.ph-rows-head { display: flex; gap: 6px; font-size: 11px; opacity: 0.6; padding: 0 30px 0 2px; }
.ph-rows-head-phrase { flex: 2; }
.ph-rows-head-freq { flex: 1; }
.ph-row { display: flex; align-items: center; gap: 6px; }
.ph-row-phrase { flex: 2; padding: 5px 8px; font-size: 13px; }
.ph-row-freq { flex: 1; padding: 5px 8px; font-size: 13px; width: 0; }
.ph-row-del { flex: 0 0 auto; border: none; background: transparent; cursor: pointer; opacity: 0.5; font-size: 14px; padding: 4px 6px; }
.ph-row-del:hover { opacity: 1; }
.ph-row-add { align-self: flex-start; }
.ph-list-actions { display: flex; flex-wrap: wrap; gap: 6px; }
.ph-list-file { display: none; }
.ph-btn { padding: 5px 12px; border: 1px solid currentColor; background: transparent; cursor: pointer; border-radius: 4px; }
.ph-btn[disabled] { opacity: 0.5; cursor: default; }
.ph-btn-ghost { opacity: 0.8; }
.ph-list-msg { margin: 0; font-size: 12px; color: #157347; }
.ph-list-err { margin: 0; font-size: 12px; color: #b02a37; }
.ph-modal-backdrop { position: fixed; inset: 0; background: rgba(0,0,0,0.4); display: flex; align-items: center; justify-content: center; z-index: 1000; }
.ph-modal { background: #fff; color: #212529; color-scheme: light; padding: 20px; border-radius: 6px; max-width: 360px; }
.ph-modal-actions { display: flex; gap: 8px; margin-top: 12px; flex-wrap: wrap; }
</style>
